import {
  AnyAssetApplication,
  AnyAssetSubType,
  assetTypeLabel,
  CustomField,
  FormikNewFormValues,
} from '@mss/mss-component-library';
import { CustomFieldModels } from '../../../asset/services/queries';
import { ExportAssetType } from '../../../asset/util/useAddPermissions';
import { Header } from './types';

export const paramsToExportAssetType = (paramType: string): ExportAssetType => {
  switch (paramType) {
    case 'security':
      return 'application';
    case 'assetmanagement':
      return 'asset_management';
    case 'analyzermanagement':
      return 'analyzer_management';
    case 'plcdcs':
      return 'dcs_plc';
    default:
      return paramType as ExportAssetType;
  }
};

export const assetModel: Record<ExportAssetType, CustomFieldModels> = {
  application: 'ApplicationAsset',
  asset_management: 'AssetManagementApplication',
  analyzer_management: 'AnalyzerManagementApplication',
  control: 'ControlApplication',
  compute: 'ComputeAsset',
  dcs_plc: 'DCSPLCAsset',
  network: 'NetworkAsset',
  field: 'FieldAsset',
  env: 'EnvAsset',
  virtualization: 'VirtualizationApplication',
};

export const paramsToSteps = (paramType: ExportAssetType) => {
  const shared = [{ label: 'Configure', value: 'settings' }];
  switch (paramType) {
    case 'field':
      return shared;
    default:
      return [
        ...shared,
        { label: 'Connect', value: 'connection' },
        { label: 'Collectors', value: 'collectors' },
      ];
  }
};

export const exportAssetTypeToCategory = (paramType: ExportAssetType) => {
  switch (paramType) {
    case 'analyzer_management':
    case 'control':
    case 'application':
    case 'asset_management':
    case 'virtualization':
      return 'application';
    default:
      return 'asset';
  }
};

// TODO: This can & should be driven from asset config metadata
export const exportAssetTypeSettingsKeys = (type: ExportAssetType) => {
  switch (type) {
    case 'analyzer_management':
      return [{ field: 'db_name', type: 'Calsys AMADAS' }];
    case 'application':
      return [
        { field: 'database_ip', type: 'WSUS' },
        { field: 'database_port', type: 'WSUS' },
        {
          field: 'api_url',
          type: 'Trellix ePO, Veeam, Claroty (CTD), Nozomi Guardian, WhatsUp Gold',
        },
      ];
    case 'asset_management':
      return [
        { field: 'port', type: 'InsightSuite AE (ISAE)' },
        { field: 'timezone', type: 'PRM' },
        { field: 'opc_server_group', type: 'PRM' },
      ];
    case 'dcs_plc':
      return [
        { field: 'domain_number', type: 'All types but FCN, Siemens' },
        { field: 'station_number', type: 'All types but FCN, Siemens' },
        { field: 'timezone', type: 'FCN' },
        { field: 'device_ip_address', type: 'HMI/STN' },
        { field: 'opc_server_group', type: 'Siemens S7, Siemens Generic' },
      ];
    default:
      return [];
  }
};

// TODO: This can & should be driven from asset config metadata
export const exportAssetTypeHeaders = (type: ExportAssetType): Header[] => {
  const subType = exportAssetTypeSubTypeKey(type);
  const defaultHeaders = [
    { key: 'name', type: 'string', required: true },
    { key: 'role', type: 'string' },
    { key: 'custodian', type: 'UUID' },
    { key: 'location', type: 'string' },
    { key: 'priority', type: 'criticality (low/medium/high)' },
    { key: 'ip_address', type: 'IPv4 address' },
    { key: 'description', type: 'string' },
    { key: subType.key, type: subType.options.join('/'), required: true },
  ];

  const specificHeaders: Partial<Record<ExportAssetType, Header[]>> = {
    analyzer_management: [{ key: 'db_name', type: 'string' }],
    application: [
      { key: 'api_url', type: 'string' },
      { key: 'database_ip', type: 'IPv4 address' },
      { key: 'database_port', type: 'number (1-65536)' },
    ],
    asset_management: [
      { key: 'port', type: 'number (1-65536)' },
      { key: 'timezone', type: 'TZ identifier (i.e. Asia/Tokyo) (PRM only)' },
      {
        key: 'opc_server_group',
        type: 'OPC server group UUID, or same {id,name} object as CSV export (PRM only)',
      },
    ],
    dcs_plc: [
      { key: 'domain_number', type: 'number' },
      { key: 'station_number', type: 'number' },
      { key: 'timezone', type: 'TZ identifier (i.e. Asia/Tokyo)' },
      { key: 'device_ip_address', type: 'IPv4 address (HMI/STN only)' },
      { key: 'opc_server_group', type: 'UUID of OPCServerGroupConfig (Siemens only)' },
    ],
    network: [
      { key: 'syslog_host', type: 'string' },
      { key: 'brand', type: 'number (see reference on import page)' },
      { key: 'series', type: 'number (see reference on import page)' },
    ],
    control: [
      { key: 'timezone', type: 'TZ identifier (i.e. Asia/Tokyo)' },
      {
        key: 'ip_addresses',
        type: 'Semicolon (";") separated IPv4 address for Historical Messages Collection Groups',
      },
    ],
  };

  return [...defaultHeaders, ...(specificHeaders[type] || [])];
};

type SubTypeKeyInfo<AssetType> = {
  key: keyof AssetType;
  options: AnyAssetSubType[];
  labels: Partial<Record<AnyAssetSubType, string>>;
};

export function exportAssetTypeSubTypeKey<
  AssetType extends AnyAssetApplication,
>(type: ExportAssetType): SubTypeKeyInfo<AssetType> {
  const values: Record<ExportAssetType, SubTypeKeyInfo<AssetType>> = {
    analyzer_management: {
      key: 'analyzermanagement_type' as keyof AssetType,
      options: ['aaims', 'calsys'],
      labels: {
        aaims: 'Yokogawa Analyzer Management Application (AAIMS)',
        calsys: 'Krohne Calsys AMADAS',
      },
    },
    virtualization: {
      key: 'virtualization_type' as keyof AssetType,
      options: ['microsoft_hyper_v'],
      labels: {
        microsoft_hyper_v: 'Microsoft Hyper-V',
      },
    },
    application: {
      key: 'application_type' as keyof AssetType,
      options: [
        'trellix_epo',
        'claroty',
        'nozomi',
        'veeam',
        'wsus',
        'whatsup_gold',
      ],
      labels: {
        trellix_epo: 'Trellix ePolicy Orchestrator',
        wsus: 'Windows Server Update Services',
        veeam: 'Veeam Backup Enterprise Manager',
        claroty: 'Claroty Continuous Threat Detection',
        nozomi: 'Nozomi Guardian',
        whatsup_gold: 'WhatsUp Gold',
      },
    },
    asset_management: {
      key: 'assetmanagement_type' as keyof AssetType,
      options: ['prm', 'isae'],
      labels: {
        prm: 'Yokogawa Plant Resource Manager (PRM)',
        isae: 'Yokogawa InsightSuite AE (ISAE)',
      },
    },
    control: {
      key: 'control_type' as keyof AssetType,
      options: ['centum_vp'],
      labels: { centum_vp: 'Yokogawa - Centum VP' },
    },
    compute: {
      key: 'compute_type' as keyof AssetType,
      options: ['windows_agentless'],
      labels: {
        windows_agentless: 'Windows Asset - Agentless',
      },
    },
    dcs_plc: {
      key: 'dcs_plc_type' as keyof AssetType,
      options: ['fcs', 'scs', 'avr', 'bcv', 'wac', 'fcn', 'hmi_stn', 'siemens_s7', 'siemens_generic'],
      labels: {
        fcs: 'Yokogawa - Field Control Station (FCS)',
        scs: 'Yokogawa - Safety Control Station (SCS)',
        avr: 'Yokogawa - Vnet Router (AVR)',
        bcv: 'Yokogawa - Bus Converter (BCV)',
        wac: 'Yokogawa - Wide Area Communication Router (WAC)',
        fcn: 'Yokogawa - Field Control Node (FCN)',
        hmi_stn: 'Yokogawa - Human Machine Interface / Station (HMI/STN)',
        siemens_s7: 'Siemens SIMATIC S7',
        siemens_generic: 'Siemens Generic',
      },
    },
    network: {
      key: 'network_type' as keyof AssetType,
      options: ['switch', 'router', 'firewall', 'timeserver'],
      labels: {
        switch: 'Switch',
        router: 'Router',
        firewall: 'Firewall',
        timeserver: 'Time Server',
      },
    },
    field: {
      key: 'field_type' as keyof AssetType,
      options: ['fieldprm'],
      labels: { fieldprm: 'Field Asset' },
    },
    env: {
      key: 'env_type' as keyof AssetType,
      options: ['odu'],
      labels: {
        odu: 'Yokogawa Online Diagnosis Unit (ODU)',
      },
    },
  };
  return values[type];
}

export const fullAssetName = (type: ExportAssetType) =>
  `${assetTypeLabel({
    type,
  } as AnyAssetApplication)} ${exportAssetTypeToCategory(type)}`;

type InitialTouched = Record<string, boolean>;
type InitialTouchedNested = InitialTouched | Record<string, InitialTouched>;
export const mapAssetsInitialTouched =
  (customFields: CustomField[]) =>
  ({ values }: FormikNewFormValues<AnyAssetApplication>) => ({
    values: Object.keys(values).reduce((acc, val) => {
      if (val === 'custom_field_values') {
        acc[val] = customFields?.reduce(
          (accCf, cf) => ({ ...accCf, [cf.slug]: true }),
          {} as InitialTouched
        );
      } else {
        acc[val] = true;
      }
      return acc;
    }, {} as InitialTouchedNested),
  });
